import { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { OptionsType, useAddressAdditionalFields, filterIconMap } from '@restworld/utils-common';
import { GoogleAutoCompleteInput } from '@restworld/ui-ds-base';
import { InputSlider } from '../../../legacy/input-slider';
import { CheckboxGroup, Heading, RadioGroup, Button } from '@restworld/ui-ds-base';

import ChipCheckboxFormField from './chip-checkbox-form-field';
import { jobTypeItems, radiusItems } from './form-fields.constants';
import { domainInterests } from '../../job-explorer-filters';
import { FilterContextValuesType } from '../../job-explorer.types';
import { OccupationSelect } from '../../job-explorer-map/job-explorer-save-research-v2/job-explorer-save-research-v2.components';
import { domainInterestAtom } from '../../job-explorer-server.atoms';
import { formatSalaryRangeText, SALARY_RANGE_MAX, SALARY_RANGE_MIN } from './salary-range-text';

export const ContractType = () => {
  const { t } = useTranslation('common');
  const quickFilterItemsCopy = useMemo(
    () =>
      [
        {
          key: 'fixed_term',
          id: 'fixed_term',
          label: t('fixed_term'),
          icon: 'DeterminatoIcon',
          Icon: filterIconMap['DeterminatoIcon'],
        },
        {
          key: 'permanent',
          id: 'permanent',
          label: t('permanent'),
          icon: 'IndeterminatoIcon',
          Icon: filterIconMap['IndeterminatoIcon'],
        },
        {
          key: 'apprenticeship_internship',
          id: 'apprenticeship_internship',
          label: t('apprenticeship_or_internship'),
          icon: 'StageTirocinioIcon',
          Icon: filterIconMap['StageTirocinioIcon'],
        },
        {
          key: 'part_time',
          id: 'part_time',
          label: 'Part time',
          icon: 'PartTimeIcon',
          Icon: filterIconMap['PartTimeIcon'],
        },
        {
          key: 'full_time',
          id: 'full_time',
          label: 'Full time',
          icon: 'FullTimeIcon',
          Icon: filterIconMap['FullTimeIcon'],
        },
      ].filter((v) => v.key !== 'part_time' && v.key !== 'full_time'),
    [t]
  );
  return (
    <ChipCheckboxFormField
      title={t('type_of_contract')}
      options={quickFilterItemsCopy}
      id="contract_type"
      customChipGroupStyles={'grid grid-cols-2 gap-2'}
    />
  );
};
export const JobType = () => {
  const id = 'job_type';
  const { control } = useFormContext();
  const { t } = useTranslation('common');
  const renderCallback = useCallback(({ field }: { field: ControllerRenderProps<FieldValues, 'job_type'> }) => {
    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      const fieldValue = field.value ? [...field.value] : [];
      const value = e.target.id;
      const newData = fieldValue.includes(value) ? fieldValue.filter((v) => v !== value) : [...fieldValue, value];
      field.onChange(newData);
    };
    return (
      <CheckboxGroup
        mode="clickable"
        options={jobTypeItems}
        className={'md:pt-2'}
        {...{
          ...field,
          onChange,
        }}
      />
    );
  }, []);
  return (
    <div className="flex gap-4 flex-col">
      <Heading>{t('total_amount_of_hours')}</Heading>
      <Controller name={id} control={control} rules={{ required: false }} render={renderCallback} />
    </div>
  );
};
export const ShiftsSection = () => {
  const { t } = useTranslation('common');

  return (
    <ChipCheckboxFormField
      key="working-time-slot"
      title={t('working_time_slot')}
      options={[
        {
          key: 'morning',
          label: t('morning'),
          id: 'morning',
        },
        {
          key: 'afternoon',
          label: t('afternoon'),
          id: 'afternoon',
        },
        {
          key: 'evening',
          label: t('evening'),
          id: 'evening',
        },
        {
          key: 'split_shift',
          label: t('split_shift'),
          id: 'split_shift',
        },
        {
          key: 'full_time',
          label: t('full_time'),
          id: 'full_time',
        },
      ]}
      id="shifts"
    />
  );
};
export const DistanceFrom = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex gap-4 flex-col">
      <Heading>{t('distance_from')}</Heading>
      <UserDefinedLocation />
      <Radius />
    </div>
  );
};
const UserDefinedLocation = () => {
  const { setValue, getValues, control } = useFormContext();
  const [address, setAddress] = useState<string>('');
  const [toggleAddressInput, addressInputToggler] = useState(false);
  const { address_lat, address_lon } = useAddressAdditionalFields({
    addressString: address,
    surface: 'worker_explorer_distance_filter',
  });
  const addressString: string = getValues('address');

  const onChange = useCallback(
    (description: string) => {
      addressInputToggler(true);
      setAddress(description);
      setValue('address', description);
    },
    [setValue]
  );

  useEffect(() => {
    const lonId = 'user_location_lon';
    if (address_lon) setValue(lonId, address_lon);
  }, [address_lon, setValue]);
  useEffect(() => {
    const latId = 'user_location_lat';
    if (address_lat) setValue(latId, address_lat);
  }, [address_lat, setValue]);
  useEffect(() => {
    if (addressString) addressInputToggler(true);
  }, [addressString]);

  return toggleAddressInput ? (
    <div className="flex gap-2">
      <Heading size={5} weight="normal">
        {addressString}
      </Heading>
      <Button
        StartIcon={PencilSquareIcon}
        variant="tertiary"
        onClick={() => {
          addressInputToggler(false);
        }}
      />
    </div>
  ) : (
    <div className="relative">
      <GoogleAutoCompleteInput
        formId="filter-address-input"
        trackingSurface="worker_explorer_distance_filter"
        key="filter-address-input"
        placeholder="Via Roma, 12, Verona, VR, Italia"
        onSuggestionSelect={({ description }) => {
          onChange(description);
        }}
        autoFocus={Boolean(!toggleAddressInput && address)}
        control={control}
        dropdownMainWrapperStyle={'max-w-menu'}
      />
      {address ? (
        <div className="absolute right-2 top-[0.6rem]">
          <Button StartIcon={XMarkIcon} variant="tertiary" onClick={() => addressInputToggler(true)} />
        </div>
      ) : null}
    </div>
  );
};
const Radius = () => <ChipCheckboxFormField options={radiusItems} id="user_location_radius" isSingleSelectMode />;
export const SalaryRange = () => {
  const id = 'salary_range';
  const { control, setValue, watch } = useFormContext();
  const { t } = useTranslation('common');
  // const { trackEvent } = useAnalytics();
  const minSalary = watch('min_salary');
  const maxSalary = watch('max_salary');
  const rangeText = useMemo(
    () => formatSalaryRangeText(minSalary, maxSalary, t('salary_any_amount')),
    [maxSalary, minSalary, t]
  );
  const renderCallback = useCallback(
    ({ field }: { field: ControllerRenderProps<FieldValues, 'salary_range'> }) => (
      <div className="px-4">
        <InputSlider
          {...{
            ...field,
            onChange: (data) => {
              field.onChange(data);
              setValue('min_salary', data[0]);
              setValue('max_salary', data[1]);
            },
            min: SALARY_RANGE_MIN,
            max: SALARY_RANGE_MAX,
            allowCross: false,
            range: true,
            step: 100,
            pushable: true,
          }}
        />
      </div>
    ),
    [setValue]
  );
  return (
    <div className="flex gap-4 flex-col">
      <Heading>{t('monthly_earnings')}</Heading>
      <Heading color="primary">{rangeText}</Heading>
      <Controller name={id} control={control} rules={{ required: false }} render={renderCallback} />
    </div>
  );
};

export const SeasonalJobPosition = () => {
  const { t } = useTranslation();
  return (
    <ChipCheckboxFormField
      title={t('transfer')}
      options={[
        {
          key: 'seasonal_only',
          id: 'true',
          label: t('job-explorer-page:seasonal_only'),
          icon: 'IslandTravelUmbrellaIcon',
          Icon: filterIconMap['IslandTravelUmbrellaIcon'],
        },
        {
          key: 'for_the_life',
          id: 'false',
          label: t('job-explorer-page:for_the_life'),
          icon: 'IndeterminatoIcon',
          Icon: filterIconMap['IndeterminatoIcon'],
        },
        {
          key: 'is_abroad_job',
          id: 'is_abroad_job',
          label: t('job-explorer-page:aboard'),
          icon: 'GlobeEuropeAfricaIcon',
          Icon: filterIconMap['GlobeEuropeAfricaIcon'],
        },
      ]}
      id="seasonal_job_position"
      isSingleSelectMode
    />
  );
};
export const YearsOfExperience = () => {
  const { t } = useTranslation('common');
  return (
    <ChipCheckboxFormField
      title={t('years_of_experience.title')}
      options={[
        {
          key: '0_to_1',
          id: '0_to_1',
          label: t('years_of_experience.zero_to_one_years'),
        },
        {
          key: '1_to_3',
          id: '1_to_3',
          label: t('years_of_experience.one_to_three_years'),
        },
        {
          key: '3_to_5',
          id: '3_to_5',
          label: t('years_of_experience.three_to_five_years'),
        },
        {
          key: '5_plus',
          id: '5_plus',
          label: t('years_of_experience.five_plus_years'),
        },
      ]}
      id="required_experience"
      isSingleSelectMode
    />
  );
};
export const DomainInterestQualificationField = () => {
  const domainInterest = useAtomValue(domainInterestAtom);
  const { watch } = useFormContext<FilterContextValuesType>();
  const selectedDomainInterests = watch('domain_interest');
  const { t } = useTranslation('common');
  const options: OptionsType[] = useMemo(
    () =>
      domainInterests.map((item) => ({
        ...item,
        id: domainInterest?.find(({ key }) => key === item.key)?.id,
      })),
    [domainInterest]
  );
  return (
    <div className="flex flex-col gap-4">
      <ChipCheckboxFormField
        customChipGroupStyles={'grid grid-cols-2 gap-2'}
        title={t('sector')}
        options={options}
        id="domain_interest"
        componentType="explorer_filter"
        testId="explorer-filter-domain-interest"
      />
      {!selectedDomainInterests?.length && (
        <div className="flex flex-col gap-2">
          <ChipCheckboxFormField
            title={t('qualification')}
            options={[
              {
                id: '51',
                key: '51',
                label: t('room_attendant'),
                disabled: true,
              },
              {
                id: '54',
                key: '54',
                label: 'Assistant',
                disabled: true,
              },
            ]}
            id=""
            titleClassName="opacity-50 pointer-events-none cursor-not-allowed"
          />
        </div>
      )}
      {!!selectedDomainInterests?.length && (
        <div className="flex flex-col gap-4 lg:w-full">
          <Heading>{t('qualification')}</Heading>
          {selectedDomainInterests.map((v) => (
            <OccupationSelect
              key={v}
              domainInterestId={v}
              customGridStyles={'grid grid-cols-2 gap-2 lg:grid-cols-4'}
              formId="occupation_ids"
            />
          ))}
        </div>
      )}
    </div>
  );
};
export const SalaryRating = () => {
  const { t } = useTranslation('common');
  return (
    <ChipCheckboxFormField
      title={t('salary')}
      options={[1, 2, 3, 4, 5].map((x) => ({ label: x.toString(), key: x.toString(), id: x.toString() }))}
      id="salary_rating"
    />
  );
};
export const CareerRating = () => {
  const { t } = useTranslation('common');
  return (
    <ChipCheckboxFormField
      title={t('career_possibilities')}
      options={[1, 2, 3, 4, 5].map((x) => ({ label: x.toString(), key: x.toString(), id: x.toString() }))}
      id="career_rating"
    />
  );
};

export const PageContentField = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<FilterContextValuesType>();

  return (
    <div>
      <h6 className="text-neutral-700 text-lg font-bold mb-4">{t('show')}</h6>
      <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:gap-3">
        <div className="w-fit">
          <Controller
            name="page_content"
            control={control}
            render={({ field }) => (
              <RadioGroup
                id="jobExplorerPageContent"
                orientation="horizontal"
                options={[
                  { label: t('job_offers_and_venue_showcases'), id: 'jps-and-venues', key: 'jps-and-venues' },
                  { label: t('only_job_offers'), id: 'only-jps', key: 'only-jps' },
                ]}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v);
                }}
              />
            )}
          />
        </div>
        {/* Intentionally a second Controller on "contract_type" (the other lives in ContractType):
            one logical field split across two visual sections, both write to the same array. */}
        <ChipCheckboxFormField
          options={[
            {
              key: 'seasonal_accommodation',
              id: 'seasonal_accommodation',
              label: t('common:offers_with_accommodation_included'),
              icon: 'AlloggioIcon',
              Icon: filterIconMap['AlloggioIcon'],
            },
          ]}
          id="contract_type"
        />
      </div>
    </div>
  );
};
